import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import AudioDetailPage from './AudioDetailPage';
import PlayerPage from './PlayerPage';
import { T } from './constants';
import { getSharedAudioDetail } from './api';
import { SharedAudioDetail } from './types';

type ViewType = 'detail' | 'player';

export default function Audio() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [view, setView] = useState<ViewType>('detail');
  const [startTime, setStartTime] = useState(0);
  const [data, setData] = useState<SharedAudioDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, shareCode]);

  // Fetch audio detail by shareCode
  useEffect(() => {
    if (!shareCode) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    getSharedAudioDetail(shareCode)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load audio');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [shareCode]);

  // Override page meta for Oasis sharing
  useEffect(() => {
    const originalTitle = document.title;

    // Save original favicon hrefs
    const faviconLinks = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
    const originalFaviconHrefs = faviconLinks.map((l) => l.href);
    const appleTouchLinks = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]'));
    const originalAppleTouchHrefs = appleTouchLinks.map((l) => l.href);

    const titleText = data?.title ? `Oasis — ${data.title}` : 'Oasis';
    const descText = data?.content ?? 'Your oasis for calm, focused audio.';

    document.title = titleText;

    // Update all favicon links (bust cache with timestamp)
    const ts = Date.now();
    faviconLinks.forEach((l) => {
      l.href = `/oasis-icon.png?v=${ts}`;
    });
    appleTouchLinks.forEach((l) => {
      l.href = `/oasis-icon.png?v=${ts}`;
    });

    // Update meta
    const setMeta = (prop: string, content: string) => {
      const el = document.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
      if (el) el.content = content;
    };
    const setMetaName = (name: string, content: string) => {
      const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (el) el.content = content;
    };

    const pageUrl = window.location.href;
    setMeta('og:title', titleText);
    setMeta('og:description', descText);
    setMeta('og:image', window.location.origin + '/oasis-icon.png');
    setMeta('og:url', pageUrl);
    setMetaName('description', descText);

    return () => {
      document.title = originalTitle;
      faviconLinks.forEach((l, i) => {
        if (originalFaviconHrefs[i]) l.href = originalFaviconHrefs[i];
      });
      appleTouchLinks.forEach((l, i) => {
        if (originalAppleTouchHrefs[i]) l.href = originalAppleTouchHrefs[i];
      });
    };
  }, [data]);

  return (
    <div
      style={{
        width: '100vw',
        minHeight: '100vh',
        background: '#0F1428',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none' as React.CSSProperties['msOverflowStyle'],
      }}
      className="audio-page-wrapper"
    >
      <style>{`
        .audio-page-wrapper,
        .audio-page-wrapper *::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .audio-page-wrapper * { scrollbar-width: none; -ms-overflow-style: none; }
        .audio-page-wrapper button { -webkit-tap-highlight-color: transparent; font-family: inherit; }
        .audio-page-wrapper a { -webkit-tap-highlight-color: transparent; }
        @media (max-width: 420px) {
          .audio-page-phone { width: 100vw !important; height: 100vh !important; box-shadow: none !important; }
        }
      `}</style>
      <div
        className="audio-page-phone"
        style={{
          width: 375,
          height: 812,
          maxWidth: '100vw',
          maxHeight: '100vh',
          background: T.background,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary, fontSize: 14 }}>
            Loading…
          </div>
        )}
        {error && !loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', background: '#1F1F1F', fontSize: 15, padding: 24, textAlign: 'center', gap: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Episode unavailable</div>
            <div style={{ fontSize: 13, color: '#BDBDBD' }}>{error}</div>
          </div>
        )}
        {!loading && !error && data && view === 'detail' && (
          <AudioDetailPage
            data={data}
            onPlay={(t) => { setStartTime(t ?? data.progress ?? 0); setView('player'); }}
          />
        )}
        {!loading && !error && data && view === 'player' && (
          <PlayerPage data={data} onBack={() => setView('detail')} initialTime={startTime} />
        )}
      </div>
    </div>
  );
}
